/**
 * useUrlFilterSync Hook
 *
 * Provides bidirectional sync between URL search params and filter state.
 * Works with TanStack Router and any filter state management (Redux, useState, etc.)
 *
 * Features:
 * - Parses URL params on mount and applies to state
 * - Updates URL when filter state changes
 * - Debounces search/text inputs to reduce URL updates
 * - Generates shareable URLs
 * - Works with domain-specific filter configurations
 *
 * @example
 * ```tsx
 * import { useUrlFilterSync } from '@ufc/core'
 * import { salesOrdersFilterConfig } from './filter-config'
 *
 * function SalesOrdersSection() {
 *   const dispatch = useDispatch()
 *   const filter = useSelector(selectFilter)
 *
 *   const { setFilter, resetFilter, hasActiveFilters } = useUrlFilterSync({
 *     config: salesOrdersFilterConfig,
 *     currentFilter: filter,
 *     onFilterChange: (updates) => dispatch(actions.setFilter(updates)),
 *     debounceMs: 300,
 *   })
 *
 *   return (
 *     <div>
 *       <input onChange={(e) => setFilter({ search: e.target.value })} />
 *       {hasActiveFilters && <button onClick={resetFilter}>Clear</button>}
 *     </div>
 *   )
 * }
 * ```
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  DomainFilterConfig,
  UseUrlFilterSyncResult,
} from './filter-config.types'
import { isActiveFilter, countActiveFilters, getActiveFilterKeys } from './filter-config.types'
import {
  generateWindowShareableUrl,
  getWindowSearchParams,
  replaceWindowSearchParams,
} from '../hooks/url-search-params'

// ============================================
// Types
// ============================================

export interface UseUrlFilterSyncOptions<TFilter extends Record<string, unknown>> {
  /** Domain filter configuration */
  config: DomainFilterConfig<TFilter>

  /** Current filter state (from Redux/useState) */
  currentFilter: TFilter

  /** Callback when filter changes (dispatch to Redux, etc.) */
  onFilterChange: (filter: TFilter) => void

  /** Debounce delay for text inputs (default: 300ms) */
  debounceMs?: number

  /** Whether to sync changes to URL (default: true) */
  syncToUrl?: boolean

  /** Whether to initialize from URL on mount (default: true) */
  initFromUrl?: boolean
}

// ============================================
// Helper Functions
// ============================================

/**
 * Get URL search params (browser-safe)
 */
function getSearchParams(): URLSearchParams {
  return getWindowSearchParams()
}

/**
 * Update URL search params without navigation
 */
function updateUrl(params: URLSearchParams): void {
  replaceWindowSearchParams(params)
}

/**
 * Generate full shareable URL
 */
function generateShareableUrl(params: URLSearchParams): string {
  return generateWindowShareableUrl(params)
}

// ============================================
// Hook
// ============================================

/**
 * Hook for bidirectional URL <-> filter state synchronization
 */
export function useUrlFilterSync<TFilter extends Record<string, unknown>>(
  options: UseUrlFilterSyncOptions<TFilter>
): UseUrlFilterSyncResult<TFilter> {
  const {
    config,
    currentFilter,
    onFilterChange,
    debounceMs = 300,
    syncToUrl = true,
    initFromUrl = true,
  } = options

  // Track whether we've initialized from URL
  const initializedRef = useRef(false)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Track last URL we wrote to avoid loops
  const lastUrlRef = useRef<string>('')

  // Track latest filter to avoid stale closure issues during rapid updates
  const latestFilterRef = useRef<TFilter>(currentFilter)
  latestFilterRef.current = currentFilter

  // Track pending updates to batch them together
  const pendingUpdatesRef = useRef<Partial<TFilter>>({})

  // ----------------------------------------
  // Initialize from URL on mount
  // ----------------------------------------
  useEffect(() => {
    // Always mark as initialized so URL sync can work
    if (initializedRef.current) return
    initializedRef.current = true

    // Skip URL parsing if disabled (loader handles it)
    if (!initFromUrl) return

    const searchParams = getSearchParams()
    const urlParams: Record<string, string | null> = {}

    // Get all URL params
    searchParams.forEach((value, key) => {
      urlParams[key] = value
    })

    // Skip if no URL params
    if (Object.keys(urlParams).length === 0) return

    // Deserialize URL params to filter
    const urlFilter = config.deserialize(urlParams)

    // Merge with defaults
    const mergedFilter = { ...config.defaults, ...urlFilter }

    // Validate if validator provided
    const validatedFilter = config.validate
      ? config.validate(mergedFilter)
      : mergedFilter

    // Check if filter differs from current
    const hasChanges = Object.keys(validatedFilter).some((key) =>
      isActiveFilter(validatedFilter[key], currentFilter[key])
    )

    if (hasChanges) {
      onFilterChange(validatedFilter as TFilter)
    }
  }, [config, initFromUrl]) // Only run on mount

  // ----------------------------------------
  // Sync filter changes to URL
  // ----------------------------------------
  useEffect(() => {
    if (!syncToUrl || !initializedRef.current) return

    // Serialize current filter to URL params
    const serialized = config.serialize(currentFilter)

    // Build new search params
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(serialized)) {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value)
      }
    }

    // Sort params for consistent comparison
    params.sort()
    const newUrlStr = params.toString()

    // Skip if URL hasn't changed
    if (newUrlStr === lastUrlRef.current) return

    // Update URL
    lastUrlRef.current = newUrlStr
    updateUrl(params)
  }, [currentFilter, config, syncToUrl])

  // ----------------------------------------
  // Set filter with optional debounce
  // Uses ref-based approach to avoid stale closure issues during rapid updates
  // ----------------------------------------
  const setFilter = useCallback(
    (updates: Partial<TFilter>) => {
      // Merge new updates with any pending updates to prevent race conditions
      pendingUpdatesRef.current = { ...pendingUpdatesRef.current, ...updates }

      // Clear any pending debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }

      // Check if update includes debounce-worthy fields (like search) with actual values
      const hasTextFields = Object.keys(updates).some((key) => {
        const field = config.fields.find((f) => f.id === key)
        if (field?.type !== 'text') return false
        const val = (updates as Record<string, unknown>)[key]
        return val !== undefined && val !== null && val !== ''
      })

      const applyUpdates = () => {
        // Use latestFilterRef to get the most current filter state
        // This prevents stale closure issues when multiple setFilter calls happen rapidly
        const newFilter = { ...latestFilterRef.current, ...pendingUpdatesRef.current }
        pendingUpdatesRef.current = {} // Clear pending updates after applying
        onFilterChange(newFilter)
      }

      if (hasTextFields && debounceMs > 0) {
        // Debounce text field updates
        debounceTimerRef.current = setTimeout(applyUpdates, debounceMs)
      } else {
        // Immediate update for other fields
        applyUpdates()
      }
    },
    [config.fields, debounceMs, onFilterChange]
  )

  // ----------------------------------------
  // Reset to defaults
  // ----------------------------------------
  const resetFilter = useCallback(() => {
    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }

    onFilterChange(config.defaults)
  }, [config.defaults, onFilterChange])

  // ----------------------------------------
  // Compute derived values
  // ----------------------------------------
  const activeFilterKeys = useMemo(
    () => getActiveFilterKeys(currentFilter, config.defaults),
    [currentFilter, config.defaults]
  )

  const activeFilterCount = useMemo(
    () => countActiveFilters(currentFilter, config.defaults),
    [currentFilter, config.defaults]
  )

  const hasActiveFilters = activeFilterCount > 0

  // ----------------------------------------
  // Generate shareable URL
  // ----------------------------------------
  const getShareableUrl = useCallback(() => {
    const serialized = config.serialize(currentFilter)
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(serialized)) {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value)
      }
    }
    return generateShareableUrl(params)
  }, [currentFilter, config])

  // ----------------------------------------
  // Generate search params
  // ----------------------------------------
  const toSearchParams = useCallback(() => {
    const serialized = config.serialize(currentFilter)
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(serialized)) {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value)
      }
    }
    return params
  }, [currentFilter, config])

  // ----------------------------------------
  // Cleanup on unmount
  // ----------------------------------------
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return {
    filter: currentFilter,
    setFilter,
    resetFilter,
    hasActiveFilters,
    activeFilterKeys,
    activeFilterCount,
    getShareableUrl,
    toSearchParams,
  }
}

// ============================================
// Standalone URL Utilities
// ============================================

/**
 * Parse URL search params using domain config
 * Use outside of React components (e.g., in route loaders)
 */
export function parseUrlFilter<TFilter extends Record<string, unknown>>(
  searchParams: URLSearchParams | Record<string, string | null>,
  config: DomainFilterConfig<TFilter>
): TFilter {
  const params =
    searchParams instanceof URLSearchParams
      ? Object.fromEntries(searchParams.entries())
      : searchParams

  const urlFilter = config.deserialize(params)
  const mergedFilter = { ...config.defaults, ...urlFilter }

  return config.validate
    ? config.validate(mergedFilter)
    : (mergedFilter as TFilter)
}

/**
 * Serialize filter to URL search params
 * Use outside of React components
 */
export function serializeFilterToUrl<TFilter extends Record<string, unknown>>(
  filter: TFilter,
  config: DomainFilterConfig<TFilter>
): URLSearchParams {
  const serialized = config.serialize(filter)
  const params = new URLSearchParams()

  for (const [key, value] of Object.entries(serialized)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, value)
    }
  }

  return params
}
